import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Loader2, PlayCircle, ShoppingBag, User, Clock, X, RotateCcw } from "lucide-react";
import api, { resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SiteLayout } from "@/components/site/SiteLayout";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ContactInstructor } from "@/components/site/ContactInstructor";

export default function MyLearning() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/learning/dashboard")).data });
  const { data: refunds, refetch: refetchRefunds } = useQuery({ queryKey: ["my-refunds"], queryFn: async () => (await api.get("/refunds/mine")).data });
  useEffect(() => { document.title = "My Learning — Epic Fade Academy"; }, []);

  if (isLoading) return <SiteLayout><div className="flex justify-center py-40"><Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" /></div></SiteLayout>;

  const courses = data?.courses || [];
  const orders = data?.orders || [];
  const refundByOrder = {};
  (refunds || []).forEach((r) => { refundByOrder[r.order_id] = r; });

  return (
    <SiteLayout>
      <div className="mx-auto max-w-7xl px-6 py-10">
        <h1 className="font-display text-4xl font-bold uppercase tracking-tighter sm:text-5xl">My Learning</h1>
        <p className="mt-2 text-zinc-400">Welcome back, {user?.name}. Pick up where you left off.</p>

        <Tabs defaultValue="courses" className="mt-8">
          <TabsList className="bg-zinc-900 border border-white/10">
            <TabsTrigger value="courses" data-testid="tab-courses">Courses</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="account" data-testid="tab-account">Account</TabsTrigger>
          </TabsList>

          <TabsContent value="courses" className="mt-6">
            {data?.recently_watched?.length > 0 && (
              <div className="mb-10">
                <h2 className="mb-4 font-display text-xl uppercase tracking-wide text-zinc-300">Continue Watching</h2>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {data.recently_watched.map((c) => <ContinueCard key={c.id} c={c} />)}
                </div>
              </div>
            )}
            <h2 className="mb-4 font-display text-xl uppercase tracking-wide text-zinc-300">All My Courses</h2>
            {courses.length === 0 ? (
              <div className="rounded-lg border border-white/10 bg-zinc-900/40 py-20 text-center">
                <ShoppingBag className="mx-auto mb-4 h-10 w-10 text-zinc-600" />
                <p className="text-zinc-400">You haven't enrolled in any courses yet.</p>
                <Link to="/store"><Button className="mt-4 bg-[#DCA846] text-black hover:bg-[#C5963B]">Browse Courses</Button></Link>
              </div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {courses.map((c) => <ContinueCard key={c.id} c={c} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <div className="overflow-hidden rounded-lg border border-white/10">
              <table className="w-full text-sm">
                <thead className="bg-zinc-900 text-left text-xs uppercase tracking-wider text-zinc-400">
                  <tr><th className="p-4">Course</th><th className="p-4">Amount</th><th className="p-4">Status</th><th className="p-4">Date</th><th className="p-4">Refund</th></tr>
                </thead>
                <tbody>
                  {orders.length === 0 ? (
                    <tr><td colSpan={5} className="p-8 text-center text-zinc-500">No orders yet.</td></tr>
                  ) : orders.map((o) => (
                    <tr key={o.id} className="border-t border-white/5" data-testid={`order-row-${o.id}`}>
                      <td className="p-4 text-white">{o.course_title}</td>
                      <td className="p-4 text-zinc-300">${Number(o.amount).toFixed(2)}</td>
                      <td className="p-4"><StatusBadge status={o.payment_status} /></td>
                      <td className="p-4 text-zinc-500">{new Date(o.created_at).toLocaleDateString()}</td>
                      <td className="p-4"><RefundCell order={o} existing={refundByOrder[o.id]} onDone={refetchRefunds} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="account" className="mt-6">
            <div className="max-w-md rounded-lg border border-white/10 bg-zinc-900/40 p-6">
              <div className="flex items-center gap-4">
                {user?.picture ? <img src={user.picture} alt="" className="h-16 w-16 rounded-full object-cover" /> :
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#DCA846]/20"><User className="h-7 w-7 text-[#DCA846]" /></div>}
                <div>
                  <p className="font-display text-lg text-white">{user?.name}</p>
                  <p className="text-sm text-zinc-400">{user?.email}</p>
                </div>
              </div>
              <div className="mt-6 space-y-2 text-sm text-zinc-400">
                <p>Role: <span className="text-white capitalize">{user?.role}</span></p>
                <p>Member since: <span className="text-white">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}</span></p>
                <p>Enrolled courses: <span className="text-white">{courses.length}</span></p>
              </div>
            </div>
            <div className="mt-6 max-w-md">
              <ContactInstructor context="" />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </SiteLayout>
  );
}

function ContinueCard({ c }) {
  const target = c.continue_lesson_id ? `/learn/${c.id}?lesson=${c.continue_lesson_id}` : `/learn/${c.id}`;
  return (
    <Link to={target} data-testid={`my-course-${c.id}`}
      className="group overflow-hidden rounded-lg border border-white/10 bg-zinc-900/50 hover:border-[#DCA846]/50 transition-all">
      <div className="relative aspect-video overflow-hidden">
        <img src={resolveImg(c.thumbnail || c.cover_image)} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
          <PlayCircle className="h-12 w-12 text-white" />
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-display text-lg text-white line-clamp-1">{c.title}</h3>
        <div className="mt-3">
          <div className="flex justify-between text-xs text-zinc-400"><span>{c.progress}% complete</span><span>{c.completed_lessons}/{c.total_lessons}</span></div>
          <Progress value={c.progress} className="mt-1 h-1.5 bg-zinc-800" />
        </div>
        <p className="mt-3 flex items-center gap-1 text-xs text-[#DCA846]"><Clock className="h-3 w-3" /> {c.progress > 0 ? "Continue watching" : "Start course"}</p>
      </div>
    </Link>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
    pending: "border-yellow-500/30 bg-yellow-500/10 text-yellow-400",
    failed: "border-red-500/30 bg-red-500/10 text-red-400",
    refunded: "border-zinc-500/30 bg-zinc-500/10 text-zinc-400",
  };
  return <span className={`rounded border px-2 py-0.5 text-xs uppercase ${map[status] || map.pending}`}>{status}</span>;
}

const REFUND_LABEL = {
  requested: { text: "Requested", cls: "text-yellow-400" },
  approved: { text: "Processing", cls: "text-blue-400" },
  refunded: { text: "Refunded", cls: "text-emerald-400" },
  rejected: { text: "Rejected", cls: "text-zinc-400" },
  failed: { text: "Failed", cls: "text-red-400" },
};

function RefundCell({ order, existing, onDone }) {
  const [open, setOpen] = useState(false);
  const [elig, setElig] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (existing) {
    const l = REFUND_LABEL[existing.status] || REFUND_LABEL.requested;
    return <span data-testid={`my-refund-status-${order.id}`} className={`text-xs font-semibold uppercase ${l.cls}`}>{l.text}</span>;
  }
  if (order.payment_status !== "paid") return <span className="text-zinc-600">—</span>;

  const openModal = async () => {
    setOpen(true); setElig(null);
    try {
      const { data } = await api.get(`/refunds/eligibility/${order.course_id}`);
      setElig(data);
    } catch { setElig({ eligible: false, reason: "Could not check eligibility. Please try again." }); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/refunds/request", { course_id: order.course_id, reason });
      toast.success("Refund request submitted");
      setOpen(false); setReason("");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={openModal} className="border-white/15 text-white" data-testid={`request-refund-${order.id}`}>
        <RotateCcw className="mr-1 h-3.5 w-3.5" /> Request
      </Button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="refund-request-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-md rounded-lg border border-white/10 bg-zinc-950 p-6 text-left">
            <button onClick={() => setOpen(false)} className="absolute right-4 top-4 text-zinc-500 hover:text-white"><X className="h-5 w-5" /></button>
            <h3 className="font-display text-lg font-bold text-white">Request a Refund</h3>
            <p className="mt-1 text-sm text-zinc-400">{order.course_title}</p>
            {!elig ? (
              <div className="py-8 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-[#DCA846]" /></div>
            ) : elig.eligible ? (
              <>
                <div className="mt-3 rounded border border-emerald-500/20 bg-emerald-500/5 p-3 text-xs text-emerald-300" data-testid="refund-eligible-note">
                  You're eligible — within {elig.window_days} days of purchase and {elig.progress}% completed (under {elig.max_progress_pct}%).
                </div>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Tell us why (optional)"
                  className="mt-4 bg-zinc-900 border-white/10 text-white" data-testid="refund-reason-input" />
                <Button onClick={submit} disabled={busy} className="mt-4 w-full bg-[#DCA846] text-black hover:bg-[#C5963B]" data-testid="refund-submit-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Refund Request"}
                </Button>
                <p className="mt-2 text-xs text-zinc-500">Requests are reviewed by our team before any refund is issued.</p>
              </>
            ) : (
              <div className="mt-4 rounded border border-red-500/20 bg-red-500/5 p-3 text-sm text-red-300" data-testid="refund-blocked-note">
                {elig.reason}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
